import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { types as at, errorAction } from "@authzsvc/api-lib";
import GroupSearchModal from "../../common/GroupSearchModal";
import TooltipIcon from "../../common/TooltipIcon";
import {
  getGroupModerators,
  addGroupModerator,
  deleteGroupModerator,
} from "../../../actions/api";
import { Button, Card, List, Modal, Table, Typography } from "antd";
import {
  DeleteOutlined,
  ReconciliationOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { showErrorNotification, showSuccessNotification } from "../../../util";

const GroupModerators = ({ canManageGroup }) => {
  const dispatch = useDispatch();
  const { id } = useParams();

  const [moderators, setModerators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [itemsToRemove, setItemsToRemove] = useState([]);

  const dataSource =
    moderators?.map((moderator) => ({
      key: moderator.id,
      groupIdentifier: moderator.groupIdentifier,
      displayName: moderator.displayName,
      description: moderator.description,
    })) || [];

  const hasSelections = selectedRows.length > 0;

  const rowSelection = {
    selectedRowKeys: selectedRows,
    onChange: (selectedRowKeys) => {
      setSelectedRows(selectedRowKeys);
    },
    getCheckboxProps: () => ({
      disabled: !canManageGroup,
    }),
  };

  const tableColumns = [
    {
      title: "Identifier",
      dataIndex: "groupIdentifier",
      key: "groupIdentifier",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Name",
      dataIndex: "displayName",
      key: "displayName",
      width: 180,
      ellipsis: true,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 220,
      ellipsis: true,
    },
  ];

  const getModerators = useCallback(async () => {
    setLoading(true);
    const result = await dispatch(getGroupModerators(id));
    const isErrorAction = result.type === errorAction(at.GET_GROUP_MODERATORS);
    if (isErrorAction) {
      showErrorNotification(
        result.payload.title ?? "Something went wrong",
        result.payload.message ?? "Could not get moderators, try again"
      );
    } else {
      setModerators(result.payload.data);
    }

    setLoading(false);
  }, [dispatch, id]);

  const handleAddModerator = async (moderatorId) => {
    setLoading(true);
    const result = await dispatch(addGroupModerator(id, moderatorId));
    const isErrorAction = result.type === errorAction(at.ADD_GROUP_MODERATOR);
    if (isErrorAction) {
      showErrorNotification(
        result.payload.title ?? "Something went wrong",
        result.payload.message ?? "Could not add moderator, try again"
      );
    } else {
      getModerators();
      showSuccessNotification("Group moderator added successfully!");
    }
    setLoading(false);
  };

  const handleDeleteModerator = async (moderatorIds) => {
    setLoading(true);

    const deletePromises = moderatorIds.map((moderatorId) =>
      dispatch(deleteGroupModerator(id, moderatorId))
    );

    const results = await Promise.all(deletePromises);

    const hasErrors = results.some(
      (result) => result.type === errorAction(at.DELETE_GROUP_MODERATOR)
    );

    if (hasErrors) {
      showErrorNotification(
        "Something went wrong",
        "Could not delete some moderators, try again"
      );
    } else {
      getModerators();
      showSuccessNotification("Group moderators removed successfully!");
    }
    setLoading(false);
  };

  const getRowKey = (moderator) => {
    return moderator?.key;
  };

  const handleRemove = () => {
    const filteredModerators = moderators
      ?.filter((moderator) => selectedRows.includes(moderator.id))
      .map((moderator) => moderator.groupIdentifier);

    setItemsToRemove(filteredModerators);
    setIsRemoveModalOpen(true);
  };

  const handleRemoveModalOk = () => {
    const moderatorIds = moderators
      ?.filter((moderator) => selectedRows.includes(moderator.id))
      .map((moderator) => moderator.groupIdentifier);

    handleDeleteModerator(moderatorIds);
    setSelectedRows([]);
    setIsRemoveModalOpen(false);
  };

  const handleRemoveModalCancel = () => {
    setIsRemoveModalOpen(false);
  };

  useEffect(() => {
    getModerators();
  }, [getModerators]);

  return (
    <>
      <Card
        style={{ marginBottom: 24 }}
        title={
          <Typography.Title level={4} style={{ margin: 0 }}>
            <ReconciliationOutlined
              style={{ marginRight: 8, color: "#bfbfbf" }}
              aria-hidden="true"
            />
            Group Moderators
            <TooltipIcon
              size={16}
              title="Members of moderator groups can read and manage all properties of the group, apart from changing the administrator group."
            />
          </Typography.Title>
        }
        extra={
          canManageGroup && (
            <GroupSearchModal
              title="Add Group Moderator"
              buttonText="Add Moderator"
              onSelect={(group) => handleAddModerator(group.id)}
              icon={<PlusOutlined />}
            />
          )
        }
      >
        <Table
          rowKey={getRowKey}
          loading={loading}
          pagination={{
            showSizeChanger: true,
            hideOnSinglePage: false,
          }}
          columns={tableColumns}
          dataSource={dataSource}
          rowSelection={rowSelection}
          aria-label="Group moderators table"
        />

        {dataSource.length > 0 && (
          <div style={{ marginTop: "-48px" }}>
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              disabled={!hasSelections || !canManageGroup}
              onClick={handleRemove}
              aria-label="Remove selected moderators"
            />
          </div>
        )}
      </Card>

      <Modal
        title={
          <>
            <DeleteOutlined
              style={{ marginRight: 8, color: "#ff4d4f" }}
              aria-hidden="true"
            />
            Confirm Deletion
          </>
        }
        open={isRemoveModalOpen}
        centered
        onOk={handleRemoveModalOk}
        onCancel={handleRemoveModalCancel}
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to remove the following group moderators?</p>
        <List
          dataSource={itemsToRemove}
          renderItem={(item) => <List.Item>{item}</List.Item>}
          bordered
          size="small"
        />
      </Modal>
    </>
  );
};

export default GroupModerators;
